import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown, Check } from 'lucide-react';

const STYLE = `
  @keyframes cs-drop { from { opacity:0; transform:translateY(-6px); } to { opacity:1; transform:translateY(0); } }
  .cs-opt { display:flex; align-items:center; justify-content:space-between; padding:0.65rem 0.9rem; cursor:pointer; font-size:0.875rem; font-weight:600; font-family:'Cairo',sans-serif; color:var(--wt-text-primary); transition:background 0.12s; gap:0.5rem; direction:rtl; }
  .cs-opt:hover { background:rgba(10,186,181,0.09); }
  .cs-opt.cs-active { background:rgba(10,186,181,0.14); color:#0ABAB5; }
`;

/**
 * CustomerSelect — portal-based dropdown, immune to overflow & z-index issues
 */
export default function CustomerSelect({ value, onChange, options = [], placeholder, style = {} }) {
  const [open, setOpen] = useState(false);
  const triggerRef      = useRef(null);
  const listRef         = useRef(null);
  const rafRef          = useRef(null);

  const selected = options.find(o => o.value === value);

  // Position the list directly on DOM — no setState → no re-render → no jitter
  const positionList = () => {
    if (!triggerRef.current || !listRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const dropHeight = listRef.current.offsetHeight;
    const spaceBelow = window.innerHeight - r.bottom;
    const above = spaceBelow < dropHeight + 12;
    const el = listRef.current;
    el.style.left  = r.left + 'px';
    el.style.width = r.width + 'px';
    if (above) {
      el.style.top    = 'auto';
      el.style.bottom = (window.innerHeight - r.top + 6) + 'px';
    } else {
      el.style.bottom = 'auto';
      el.style.top    = (r.bottom + 6) + 'px';
    }
  };

  const handleOpen = () => {
    setOpen(p => !p);
  };

  // After open → position immediately, then track scroll/resize via rAF
  useEffect(() => {
    if (!open) { cancelAnimationFrame(rafRef.current); return; }

    // first position after paint
    requestAnimationFrame(positionList);

    const onScroll = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(positionList);
    };

    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
      cancelAnimationFrame(rafRef.current);
    };
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        listRef.current    && !listRef.current.contains(e.target)
      ) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <>
      <style>{STYLE}</style>
      <div ref={triggerRef} style={{ position: 'relative', ...style }}>
        <button
          type="button"
          onClick={handleOpen}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: '0.5rem', padding: '0.72rem 0.9rem', borderRadius: '0.75rem',
            border: open ? '1.5px solid rgba(10,186,181,0.7)' : '1.5px solid var(--wt-border)',
            background: 'rgba(255,255,255,0.05)', color: 'var(--wt-text-primary)',
            fontSize: '0.875rem', fontWeight: 600, fontFamily: "'Cairo', sans-serif",
            cursor: 'pointer', outline: 'none', textAlign: 'right',
            boxShadow: open ? '0 0 0 3px rgba(10,186,181,0.12)' : 'none',
            transition: 'border-color 0.15s, box-shadow 0.15s',
          }}
        >
          <span style={{ flex: 1, textAlign: 'right', opacity: selected ? 1 : 0.45 }}>
            {selected ? selected.label : (placeholder || 'اختر...')}
          </span>
          <ChevronDown size={15} style={{ flexShrink: 0, color: 'var(--wt-text-muted)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
        </button>
      </div>

      {open && ReactDOM.createPortal(
        <div ref={listRef} style={{
          position: 'fixed', zIndex: 99999,
          left: 0, top: 0, width: 0,          /* real values set by positionList() */
          borderRadius: '0.875rem',
          background: 'var(--wt-surface)',
          border: '1.5px solid var(--wt-border)',
          boxShadow: '0 12px 40px rgba(0,0,0,0.28)',
          overflow: 'hidden',
          animation: 'cs-drop 0.15s ease',
          direction: 'rtl',
        }}>
          {options.map(opt => (
            <div
              key={opt.value}
              className={`cs-opt${opt.value === value ? ' cs-active' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); onChange(opt.value); setOpen(false); }}
            >
              <span style={{ flex: 1, textAlign: 'right' }}>{opt.label}</span>
              {opt.value === value && <Check size={14} style={{ flexShrink: 0, color: '#0ABAB5' }} />}
            </div>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}
